import React from 'react';
import { Zap, Users, Building2, Target, ArrowRight, FileText, MapPin, Newspaper, Mic } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SEOHead } from '../components/SEOHead';
import CommunityMap from '../components/CommunityMap';
import { useNavigate } from 'react-router-dom';

export default function NosRealisationsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const stats = [
    { icon: Zap, value: '1200+ MWh', label: t('realisations.stats.shared') },
    { icon: Building2, value: '80+', label: t('realisations.stats.members') },
    { icon: Users, value: '200+', label: t('realisations.stats.meters') },
    { icon: Target, value: '5 GWh', label: t('realisations.stats.goal') },
  ];

  return (
    <>
      <SEOHead
        title="Nos Réalisations | Sun Is Up - Communauté d'Énergie Bruxelles"
        description="Découvrez notre communauté d'énergie bruxelloise en action : 1200+ MWh partagés, 80+ entreprises membres, 200+ compteurs actifs."
        keywords="réalisations communauté énergie bruxelles, résultats partage énergie, statistiques sun is up, communauté énergie locale bruxelles"
        url="https://sunisup.be/nos-realisations"
        logo="https://sunisup.be/images/logo.png"
      />
      <div className="min-h-screen bg-white font-sans">
        {/* Hero */}
        <section className="py-20 lg:py-32" style={{ backgroundColor: '#FFF5E1' }}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold mb-6" style={{ color: '#1a1a1a' }}>
              {t('realisations.hero.title')}
            </h1>
            <p className="text-lg sm:text-xl lg:text-2xl max-w-3xl mx-auto" style={{ color: '#4a4a4a' }}>
              {t('realisations.hero.subtitle')}
            </p>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 sm:py-24 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold" style={{ color: '#1a1a1a' }}>
                {t('realisations.stats.title')}
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {stats.map((stat, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 text-center" style={{ border: '1px solid #e5e5e5' }}>
                  <div className="w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(248, 189, 61, 0.15)' }}>
                    <stat.icon className="w-8 h-8" style={{ color: '#F8BD3D' }} />
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: '#1a1a1a' }}>{stat.value}</h3>
                  <p className="text-sm font-medium" style={{ color: '#666666' }}>{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Press & Media */}
        <section className="py-16 sm:py-24" style={{ backgroundColor: '#fafafa' }}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4" style={{ color: '#1a1a1a' }}>
                {t('realisations.press.title')}
              </h2>
              <p className="text-lg max-w-2xl mx-auto" style={{ color: '#4a4a4a' }}>
                {t('realisations.press.desc')}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Trends */}
              <div className="rounded-2xl p-8 text-center flex flex-col items-center" style={{ backgroundColor: 'white', border: '1px solid #e5e5e5' }}>
                <div className="w-14 h-14 rounded-lg flex items-center justify-center mb-5" style={{ backgroundColor: 'rgba(248, 189, 61, 0.15)' }}>
                  <FileText className="w-7 h-7" style={{ color: '#F8BD3D' }} />
                </div>
                <h3 className="text-lg font-bold mb-3" style={{ color: '#1a1a1a' }}>Trends</h3>
                <p className="text-sm mb-6 flex-grow" style={{ color: '#666666' }}>
                  {t('realisations.press.trendsDesc')}
                </p>
                <a
                  href="https://lnkd.in/p/endnhbbX"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 rounded-full font-semibold transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5 inline-flex items-center gap-2 text-sm"
                  style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
                >
                  {t('realisations.press.button')}
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
              {/* BECI */}
              <div className="rounded-2xl p-8 text-center flex flex-col items-center" style={{ backgroundColor: 'white', border: '1px solid #e5e5e5' }}>
                <div className="w-14 h-14 rounded-lg flex items-center justify-center mb-5" style={{ backgroundColor: 'rgba(248, 189, 61, 0.15)' }}>
                  <Newspaper className="w-7 h-7" style={{ color: '#F8BD3D' }} />
                </div>
                <h3 className="text-lg font-bold mb-3" style={{ color: '#1a1a1a' }}>BECI</h3>
                <p className="text-sm mb-6 flex-grow" style={{ color: '#666666' }}>
                  {t('realisations.press.beciDesc')}
                </p>
                <a
                  href="https://www.beci.be/blog/esg-34/partage-d-energie-reprendre-la-maitrise-d-une-partie-de-sa-facture-energetique-4582"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 rounded-full font-semibold transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5 inline-flex items-center gap-2 text-sm"
                  style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
                >
                  {t('realisations.press.button')}
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
              {/* BXFM */}
              <div className="rounded-2xl p-8 text-center flex flex-col items-center" style={{ backgroundColor: 'white', border: '1px solid #e5e5e5' }}>
                <div className="w-14 h-14 rounded-lg flex items-center justify-center mb-5" style={{ backgroundColor: 'rgba(248, 189, 61, 0.15)' }}>
                  <Mic className="w-7 h-7" style={{ color: '#F8BD3D' }} />
                </div>
                <h3 className="text-lg font-bold mb-3" style={{ color: '#1a1a1a' }}>BXFM Radio</h3>
                <p className="text-sm mb-6 flex-grow" style={{ color: '#666666' }}>
                  {t('realisations.press.bxfmDesc')}
                </p>
                <a
                  href="https://podcast.ausha.co/bxfm-radio/podcast-sun-is-up-thomas-josse-www-sunisup-be"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 rounded-full font-semibold transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5 inline-flex items-center gap-2 text-sm"
                  style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
                >
                  {t('realisations.press.button')}
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Carte de notre communauté */}
        <section className="py-16 sm:py-24 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(248, 189, 61, 0.2)' }}>
                <MapPin className="w-6 h-6" style={{ color: '#F8BD3D' }} />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold" style={{ color: '#1a1a1a' }}>
                {t('realisations.map.title')}
              </h2>
            </div>
            <p className="text-lg mb-8 max-w-3xl" style={{ color: '#4a4a4a' }}>
              {t('realisations.map.desc')}
            </p>
            <CommunityMap />
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-24" style={{ backgroundColor: '#FFF5E1' }}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ color: '#1a1a1a' }}>
              {t('realisations.cta.title')}
            </h2>
            <p className="text-xl mb-10" style={{ color: '#4a4a4a' }}>
              {t('realisations.cta.subtitle')}
            </p>
            <button
              onClick={() => navigate('/contact')}
              className="px-10 py-5 rounded-full font-bold text-lg transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1 inline-flex items-center gap-3"
              style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
            >
              {t('realisations.cta.button')}
              <ArrowRight className="w-6 h-6" />
            </button>
          </div>
        </section>
      </div>
    </>
  );
}
